import React, { useMemo, useRef, useState } from "react";
import { Form } from "react-router-dom";
import { toast } from "react-toastify";
import EditGameMutation from "./mutation/EditGame";
import {
    convertInputToUTC,
    formattedDateTime,
    inputFormatDateTime,
} from "../../util/date";
import useThemeColors from "../organization/portal/useThemeColors";
import { alphaFromHex } from "../../util/colors";
import useTranslation from "../../i18n/useTranslation";
import { getLocalizedGraphQLError } from "../../util/graphqlError";

const EMPTY_LIST = [];

export default function EditGameModal({ games, stages, event, managers, assignmentHistory = [] }) {
    const { t, language } = useTranslation();
    const colors = useThemeColors();
    const [editGame] = EditGameMutation();
    const [validationErrors, setValidationErrors] = useState(null);

    const game = event.game;
    const safeStages = Array.isArray(stages) ? stages : EMPTY_LIST;
    const safeManagers = Array.isArray(managers) ? managers : EMPTY_LIST;
    const safeAssignmentHistory = Array.isArray(assignmentHistory) ? assignmentHistory : EMPTY_LIST;
    const safeResponsibles = Array.isArray(event.responsibles) ? event.responsibles : EMPTY_LIST;
    const safeEventManagers = Array.isArray(event.managers)
        ? event.managers.map((manager) => (
            manager && typeof manager === "object" ? manager : { id: manager }
        ))
        : EMPTY_LIST;

    const [stage] = useState(safeStages.find((s) => s.id === event.stage?.id) || event.stage || {});
    const [teams] = useState(stage.teams || EMPTY_LIST);
    const [fields] = useState(stage.fields || EMPTY_LIST);

    const stageInput = useRef(null);
    const homeTeamInput = useRef(null);
    const visitorTeamInput = useRef(null);
    const dateTimeInput = useRef(null);
    const descriptionInput = useRef(null);
    const fieldInput = useRef(null);
    const refereeInput = useRef(null);
    const managerInput = useRef(null);

    const modalId = useMemo(() => `edit-game-modal${game?.id}`, [game?.id]);
    const scopeSel = useMemo(() => `#${modalId}`, [modalId]);

    const coachLabel =
        event.stage?.scheduleType === "TRAINING"
            ? t("editGameModal.labels.coach")
            : t("editGameModal.labels.refereeUmpire");

    const assignedReferees = safeResponsibles;
    const stageReferees = Array.isArray(event.stage?.stageReferees) ? event.stage.stageReferees : EMPTY_LIST;
    const stageRefereeIds = new Set(stageReferees.map((referee) => String(referee.id)));
    const refereeOptions = [...stageReferees, ...assignedReferees.filter((referee) => !stageRefereeIds.has(String(referee.id)))];

    const managerLabel = t("editGameModal.labels.manager");

    function handleSubmit(e) {
        e.preventDefault();

        const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const utcDatetime = convertInputToUTC(dateTimeInput.current.value, timezone);

        editGame({
            variables: {
                gameId: game.id,
                fieldId: fieldInput.current.value,
                homeTeamId: homeTeamInput.current.value,
                visitorTeamId: visitorTeamInput.current.value,
                dateTime: utcDatetime,
                timezone,
                description: descriptionInput.current.value || null,
                refereeIds: Array.from(refereeInput.current.selectedOptions).map(o => o.value),
                managerIds: Array.from(managerInput.current.selectedOptions).map(o => o.value),
            },
        })
            .then((r) => {
                if (r.data?.editGame?.success) {
                    toast.success(t("editGameModal.messages.gameModifiedSuccessfully"), {
                        autoClose: 3000,
                        toastId: "edit_added",
                        position: "top-center",
                        closeOnClick: true,
                    });
                    setTimeout(() => window.location.reload(), 3000);
                } else {
                    setValidationErrors({
                        message: r.data?.editGame?.message,
                        errorData: r.data?.editGame?.errorData,
                    });
                }
            })
            .catch((err) => {
                toast.error(getLocalizedGraphQLError(err, t, "errors.somethingWentWrong"), {
                    autoClose: 3000,
                    toastId: "edit_game_error",
                    position: "top-center",
                    closeOnClick: true,
                });
            });
    }

    return (
        <div className="modal fade kbola-modal" id={modalId} tabIndex="-1" role="dialog">
            <div className="modal-dialog modal-xl" role="document">
                <div className="modal-content kbola-modalContent">
                    <Form id="editGame" method="post" onSubmit={handleSubmit}>
                        <div className="modal-header kbola-modalHeader">
                            <div className="d-flex align-items-center" style={{ gap: 10, minWidth: 0 }}>
                                <span className="kbola-pill kbola-pillBrand">
                                    {t("editGameModal.edit")}
                                </span>
                                <h4 className="modal-title mb-0 kbola-modalTitle">
                                    {t("editGameModal.title")}
                                </h4>
                            </div>

                            <button
                                type="button"
                                className="close kbola-closeBtn"
                                data-dismiss="modal"
                                aria-label={t("editGameModal.close")}
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                        </div>

                        <div className="modal-body kbola-modalBody">
                            {validationErrors && (
                                <div className="kbola-alertDanger">
                                    <div className="kbola-alertTitle">
                                        <i className="icon fas fa-ban mr-2" />
                                        {validationErrors.message}
                                    </div>

                                    {validationErrors.errorData?.length > 0 && (
                                        <ul className="kbola-alertList">
                                            {validationErrors.errorData.map((s, index) => (
                                                <li key={index}>
                                                    {t("editGameModal.validation.conflictOn", {
                                                        name: s.name,
                                                        date: formattedDateTime(
                                                            s.event?.date,
                                                            s.event?.timezone,
                                                            language
                                                        ),
                                                        field: s.event?.field?.name,
                                                    })}
                                                </li>
                                            ))}
                                        </ul>
                                    )}
                                </div>
                            )}

                            {assignmentHistory.length > 0 && (
                                <div className="kbola-assignment-history mb-3">
                                    <div className="kbola-assignment-history-title">
                                        <i className="fas fa-history mr-2" />
                                        {t("officialAssignmentModal.labels.assignmentHistory")}
                                    </div>
                                    {safeAssignmentHistory.map((assignment) => {
                                        const status = String(assignment.status || "").toLowerCase();
                                        const timestamp = status === "completed"
                                            ? assignment.completedAt || assignment.respondedAt || assignment.invitedAt || assignment.createdAt
                                            : assignment.respondedAt || assignment.invitedAt || assignment.createdAt;
                                        const assignedByName = [
                                            assignment.assignedBy?.firstName,
                                            assignment.assignedBy?.lastName,
                                        ].filter(Boolean).join(" ") || assignment.assignedBy?.email;
                                        return (
                                            <div className="kbola-assignment-history-row" key={assignment.id}>
                                                <span className="font-weight-bold">{assignment.referee?.fullName || t("officialAssignmentModal.labels.referee")}</span>
                                                <span className={`kbola-assignment-history-status is-${status}`}>
                                                    {t(`officialAssignmentModal.statuses.${status}`)}
                                                </span>
                                                {timestamp && <span className="text-muted">{formattedDateTime(timestamp, event.timezone, language)}</span>}
                                                {assignedByName && <span className="text-muted">{assignedByName}</span>}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label className="kbola-label">
                                            {t("editGameModal.labels.stage")}
                                        </label>
                                        <select
                                            className="form-control select2bs4 kbola-control"
                                            name="stage"
                                            required
                                            id="editStageId"
                                            defaultValue={stage.id}
                                            disabled
                                            ref={stageInput}
                                        >
                                            <option>{t("editGameModal.placeholders.chooseStage")}</option>
                                            {safeStages.map((s) => (
                                                <option key={s.id} value={s.id}>
                                                    {s.name}
                                                </option>
                                            ))}
                                        </select>
                                        <small className="kbola-helpText">
                                            {t("editGameModal.helpText.stageLocked")}
                                        </small>
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label className="kbola-label">
                                            {t("editGameModal.labels.datetime")}
                                        </label>
                                        <input
                                            type="datetime-local"
                                            className="form-control kbola-control"
                                            required
                                            id="dateTimeInput"
                                            name="datetime"
                                            placeholder={t("editGameModal.placeholders.enterDatetime")}
                                            ref={dateTimeInput}
                                            defaultValue={inputFormatDateTime(event.date, event.timezone)}
                                            onChange={(e) => {
                                                if (e.target.value !== "") {
                                                    dateTimeInput.current.className =
                                                        "form-control kbola-control is-valid";
                                                }
                                            }}
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label className="kbola-label">
                                            {t("editGameModal.labels.visitorTeam")}
                                        </label>
                                        <select
                                            className="form-control select2bs4 kbola-control"
                                            name="visitorTeam"
                                            required
                                            id="visitorTeamId"
                                            defaultValue={game.visitorTeam?.id || game.visitorTeamAlias}
                                            ref={visitorTeamInput}
                                            onChange={(ev) => {
                                                if (
                                                    ev.target.value !== "" &&
                                                    ev.target.value !== homeTeamInput.current.value
                                                ) {
                                                    visitorTeamInput.current.className =
                                                        "form-control select2bs4 kbola-control is-valid";
                                                    homeTeamInput.current.className =
                                                        "form-control select2bs4 kbola-control is-valid";
                                                } else {
                                                    visitorTeamInput.current.className =
                                                        "form-control select2bs4 kbola-control is-invalid";
                                                    homeTeamInput.current.className =
                                                        "form-control select2bs4 kbola-control is-invalid";
                                                }
                                            }}
                                        >
                                            <option>
                                                {t("editGameModal.placeholders.chooseVisitorTeam")}
                                            </option>
                                            {teams?.map((tItem) => (
                                                <option key={tItem.teamId} value={tItem.teamId}>
                                                    {tItem.teamName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label className="kbola-label">
                                            {t("editGameModal.labels.homeTeam")}
                                        </label>
                                        <select
                                            className="form-control select2bs4 kbola-control"
                                            name="homeTeam"
                                            required
                                            id="homeTeamId"
                                            defaultValue={game.homeTeam?.id || game.homeTeamAlias}
                                            ref={homeTeamInput}
                                            onChange={(ev) => {
                                                if (
                                                    ev.target.value !== "" &&
                                                    ev.target.value !== visitorTeamInput.current.value
                                                ) {
                                                    homeTeamInput.current.className =
                                                        "form-control select2bs4 kbola-control is-valid";
                                                    visitorTeamInput.current.className =
                                                        "form-control select2bs4 kbola-control is-valid";
                                                } else {
                                                    homeTeamInput.current.className =
                                                        "form-control select2bs4 kbola-control is-invalid";
                                                    visitorTeamInput.current.className =
                                                        "form-control select2bs4 kbola-control is-invalid";
                                                }
                                            }}
                                        >
                                            <option>
                                                {t("editGameModal.placeholders.chooseHomeTeam")}
                                            </option>
                                            {teams?.map((tItem) => (
                                                <option key={tItem.teamId} value={tItem.teamId}>
                                                    {tItem.teamName}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label className="kbola-label">{coachLabel}</label>
                                        <div className="kbola-referee-picker-wrap">
                                            <select
                                                className="form-control kbola-referee-picker"
                                                name="referee"
                                                multiple
                                                defaultValue={safeResponsibles.map(r => r.id)}
                                                ref={refereeInput}
                                            >
                                                {refereeOptions.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.fullName}{!stageRefereeIds.has(String(s.id)) ? ` (${t("editGameModal.labels.notInStagePool")})` : ""}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label className="kbola-label">{managerLabel}</label>
                                        <div className="kbola-manager-picker-wrap">
                                            <select
                                                className="form-control kbola-manager-picker"
                                                name="manager"
                                                multiple
                                                defaultValue={safeEventManagers.map(m => m.id)}
                                                ref={managerInput}
                                            >
                                                {safeManagers.map((s) => (
                                                    <option key={s.id} value={s.id}>
                                                        {s.fullName}
                                                    </option>
                                                ))}
                                            </select>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div className="row">
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label className="kbola-label">
                                            {t("editGameModal.labels.facilityField")}
                                        </label>
                                        <select
                                            className="form-control select2bs4 kbola-control"
                                            name="field"
                                            required
                                            id="fieldId"
                                            defaultValue={event.field.id}
                                            ref={fieldInput}
                                            onChange={(ev) => {
                                                if (ev.target.value !== "") {
                                                    fieldInput.current.className =
                                                        "form-control select2bs4 kbola-control is-valid";
                                                }
                                            }}
                                        >
                                            <option>{t("editGameModal.placeholders.chooseField")}</option>
                                            {fields.map((s) => (
                                                <option key={s.id} value={s.id}>
                                                    {s.name}
                                                </option>
                                            ))}
                                        </select>
                                    </div>
                                </div>
                                <div className="col-sm-6">
                                    <div className="form-group">
                                        <label className="kbola-label">
                                            {t("editGameModal.labels.description")}
                                        </label>
                                        <textarea
                                            className="form-control kbola-control"
                                            id="descriptionInput"
                                            name="description"
                                            placeholder={t("editGameModal.placeholders.enterDescription")}
                                            ref={descriptionInput}
                                            defaultValue={event.description || ""}
                                            rows={3}
                                            style={{ height: "auto", resize: "vertical" }}
                                        />
                                    </div>
                                </div>
                            </div>
                        </div>

                        <div className="modal-footer kbola-modalFooter justify-content-between">
                            <button
                                type="button"
                                className="btn btn-default kbola-secondaryBtn"
                                data-dismiss="modal"
                            >
                                {t("editGameModal.close")}
                            </button>
                            <button type="submit" className="btn btn-kbola kbola-primaryBtn">
                                {t("editGameModal.save")}
                            </button>
                        </div>
                    </Form>
                </div>
            </div>

            <style>{`
        ${scopeSel} { font-family: 'Poppins', sans-serif; }
        
        ${scopeSel} .modal-dialog {
  max-height: calc(100% - 3.5rem);
}

${scopeSel} .modal-content {
  max-height: 90vh;
  overflow: hidden;
  border-radius: 18px;
  display: flex;
  flex-direction: column;
}

${scopeSel} .kbola-modalBody {
  max-height: calc(100vh - 210px);
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

        ${scopeSel} i.fas,
        ${scopeSel} i.far,
        ${scopeSel} i.fal,
        ${scopeSel} i.fa,
        ${scopeSel} i[class^="fa-"],
        ${scopeSel} i[class*=" fa-"] {
          font-family: "Font Awesome 5 Free" !important;
          font-weight: 900;
        }
        ${scopeSel} i.fab {
          font-family: "Font Awesome 5 Brands" !important;
          font-weight: 400;
        }

        ${scopeSel} .kbola-modalContent {
          border-radius: 18px;
          overflow: hidden;
          border: 1px solid rgba(0,0,0,0.08);
          box-shadow: 0 18px 44px rgba(0,0,0,0.18);
        }

        ${scopeSel} .kbola-modalHeader {
          padding: 14px 16px;
          background: linear-gradient(
                    135deg,
                    ${alphaFromHex(colors.primary, 0.50)},
                    ${alphaFromHex(colors.primary, 0.00)}
                );
          border-bottom: 1px solid rgba(0,0,0,0.06);
        }

        ${scopeSel} .kbola-modalTitle {
          font-size: 16px;
          font-weight: 900;
          color: ${colors.textOnPrimary};
          overflow: hidden;
          text-overflow: ellipsis;
          white-space: nowrap;
        }

        ${scopeSel} .kbola-closeBtn { color: ${colors.textOnPrimary}; opacity: 1; }

        ${scopeSel} .kbola-modalBody { background: #fff; padding-top: 14px; }

        ${scopeSel} .kbola-modalFooter {
          padding: 12px 14px;
          border-top: 1px solid rgba(0,0,0,0.06);
          background: rgba(0,0,0,0.01);
        }

        ${scopeSel} .kbola-pill {
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 6px 10px;
          border-radius: 999px;
          font-weight: 900;
          font-size: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          line-height: 1;
          white-space: nowrap;
        }
        ${scopeSel} .kbola-pillBrand {
          background: ${colors.primary};
          color: ${colors.textOnPrimary};
          border-color: rgba(0,0,0,0.10);
        }

        ${scopeSel} .kbola-alertDanger {
          border-radius: 14px;
          border: 1px solid rgba(220,53,69,0.18);
          background: rgba(220,53,69,0.06);
          padding: 12px 14px;
          margin-bottom: 14px;
        }
        ${scopeSel} .kbola-alertTitle {
          font-weight: 900;
          color: rgba(220,53,69,0.95);
          margin-bottom: 6px;
        }
        ${scopeSel} .kbola-alertList {
          margin: 0;
          padding-left: 18px;
          color: rgba(20,21,26,0.85);
          font-weight: 700;
          font-size: 13px;
        }

        ${scopeSel} .kbola-label {
          font-weight: 900;
          color: rgba(20,21,26,0.80);
          font-size: 13px;
          margin-bottom: 6px;
        }

        ${scopeSel} .kbola-control {
          border-radius: 12px;
          border: 1px solid rgba(0,0,0,0.10);
          font-weight: 800;
          font-size: 13px;
          padding: 10px 10px;
          box-shadow: none;
          height: 42px;
        }

        ${scopeSel} .kbola-control:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.18);
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.75);
        }

        ${scopeSel} .kbola-helpText {
          display: block;
          margin-top: 6px;
          font-size: 12px;
          font-weight: 800;
          color: rgba(20,21,26,0.55);
        }

        ${scopeSel} .kbola-primaryBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
          background: ${colors.primary};
          color: ${colors.textOnPrimary};
        }
        
        ${scopeSel} .kbola-secondaryBtn {
          border-radius: 12px;
          font-weight: 900;
          padding: 10px 14px;
        }

        ${scopeSel} .kbola-referee-picker-wrap,
        ${scopeSel} .kbola-manager-picker-wrap {
          border-radius: 12px;
          border: 1px solid rgba(0, 0, 0, 0.11);
          background: linear-gradient(180deg, rgba(255,255,255,0.94), rgba(247,248,250,0.92));
          padding: 8px;
        }

        ${scopeSel} .kbola-referee-picker,
        ${scopeSel} .kbola-manager-picker {
          min-height: 132px;
          height: auto !important;
          border: none !important;
          background: transparent !important;
          box-shadow: none !important;
          padding: 0 !important;
        }

        ${scopeSel} .kbola-referee-picker option,
        ${scopeSel} .kbola-manager-picker option {
          border-radius: 9px;
          margin: 2px 0;
          padding: 9px 10px;
          font-weight: 700;
        }

        ${scopeSel} .kbola-referee-picker option:checked,
        ${scopeSel} .kbola-manager-picker option:checked {
          background: rgba(var(--kbs-primary-rgb, 0,173,255), 0.72);
          color: #14151a;
        }

        ${scopeSel} .kbola-referee-picker-wrap:focus-within,
        ${scopeSel} .kbola-manager-picker-wrap:focus-within {
          border-color: rgba(var(--kbs-primary-rgb, 0,173,255), 0.95);
          box-shadow: 0 0 0 0.22rem rgba(var(--kbs-primary-rgb, 0,173,255), 0.22);
        }

        ${scopeSel} .kbola-referee-picker::-webkit-scrollbar,
        ${scopeSel} .kbola-manager-picker::-webkit-scrollbar {
          width: 10px;
        }

        ${scopeSel} .kbola-referee-picker::-webkit-scrollbar-thumb,
        ${scopeSel} .kbola-manager-picker::-webkit-scrollbar-thumb {
          border-radius: 999px;
          border: 2px solid rgba(255,255,255,0.95);
          background: rgba(20, 21, 26, 0.24);
        }

        ${scopeSel} .kbola-assignment-history {
          border: 1px solid #dce9f5;
          border-radius: 12px;
          padding: 12px 14px;
          background: #f7fbff;
        }

        ${scopeSel} .kbola-assignment-history-title {
          margin-bottom: 8px;
          color: #17202c;
          font-size: 13px;
          font-weight: 900;
        }

        ${scopeSel} .kbola-assignment-history-title i {
          color: ${colors.primary};
        }

        ${scopeSel} .kbola-assignment-history-row {
          display: flex;
          align-items: center;
          flex-wrap: wrap;
          gap: 8px;
          padding: 7px 0;
          border-top: 1px solid rgba(20,21,26,.07);
          color: #344054;
          font-size: 12px;
        }

        ${scopeSel} .kbola-assignment-history-status {
          padding: 3px 7px;
          border-radius: 999px;
          background: rgba(0,173,255,.10);
          color: ${colors.primary};
          font-size: 10px;
          font-weight: 900;
        }

        ${scopeSel} .kbola-assignment-history-row .text-muted {
          font-size: 11px;
        }
      `}</style>
        </div>
    );
}
